import { useAccount, type Account } from "$src/stores/use-account";
import { useFilters } from "$src/stores/use-filters";
import { cx } from "$src/lib/utils";
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
} from "@floating-ui/react";
import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";

import styles from "./split-account-nav.module.css";

const ALL_ID = -1;

type Option = {
  label: string;
  accountBrandId: number;
  active: boolean;
};

type SegmentProps = {
  label: string;
  options: Option[];
  onSelect: (accountBrandId: number) => void;
};

const Segment = ({ label, options, onSelect }: SegmentProps) => {
  const [open, setOpen] = useState(false);
  const disabled = options.length <= 1;

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-end",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  return (
    <>
      <button
        ref={refs.setReference}
        className={cx(
          styles.trigger,
          open && styles.triggerOpen,
          disabled && styles.triggerDisabled,
        )}
        disabled={disabled}
        {...getReferenceProps()}
      >
        {label}
        {!disabled && (
          <ChevronDown
            size={14}
            className={cx(styles.chevron, open && styles.chevronOpen)}
          />
        )}
      </button>

      {open && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={styles.dropdown}
            {...getFloatingProps()}
          >
            {options.map((option) => (
              <button
                key={option.accountBrandId}
                className={cx(
                  styles.option,
                  option.active && styles.optionActive,
                )}
                onClick={() => {
                  onSelect(option.accountBrandId);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

const pickAccount = (
  accounts: Account[],
  active: Account,
  overrides: { brand?: string; categoryId?: number; geographyId?: number },
) => {
  const brand = overrides.brand ?? active.brandName;
  const categoryId = overrides.categoryId ?? active.category.id;
  const geographyId = overrides.geographyId ?? active.geography.id;

  return (
    accounts.find(
      (a) =>
        a.brandName === brand &&
        a.category.id === categoryId &&
        a.geography.id === geographyId,
    ) ??
    accounts.find(
      (a) =>
        a.brandName === brand &&
        (overrides.categoryId !== undefined
          ? a.category.id === categoryId
          : overrides.geographyId !== undefined
            ? a.geography.id === geographyId
            : true),
    ) ??
    accounts.find((a) => a.brandName === brand)
  );
};

export const SplitAccountNav = () => {
  const active = useAccount((s) => s.active);
  const accounts = useAccount((s) => s.accounts);
  const activate = useAccount((s) => s.activate);
  const resetFilters = useFilters((s) => s.reset);
  const [allCategories, setAllCategories] = useState(false);
  const [allRegions, setAllRegions] = useState(false);

  const brandOptions = useMemo<Option[]>(() => {
    if (!active) return [];
    const seen = new Map<string, Option>();
    for (const acc of accounts) {
      if (seen.has(acc.brandName)) continue;
      seen.set(acc.brandName, {
        label: acc.brandName,
        accountBrandId: acc.accountBrandId,
        active: acc.brandName === active.brandName,
      });
    }
    return Array.from(seen.values());
  }, [accounts, active]);

  const categoryOptions = useMemo<Option[]>(() => {
    if (!active) return [];
    const seen = new Map<number, Option>();
    for (const acc of accounts) {
      if (acc.brandName !== active.brandName) continue;
      if (seen.has(acc.category.id)) continue;
      seen.set(acc.category.id, {
        label: acc.category.name,
        accountBrandId: acc.accountBrandId,
        active: !allCategories && acc.category.id === active.category.id,
      });
    }
    const opts = Array.from(seen.values());
    if (opts.length > 1) {
      opts.unshift({
        label: "All categories",
        accountBrandId: ALL_ID,
        active: allCategories,
      });
    }
    return opts;
  }, [accounts, active, allCategories]);

  const geographyOptions = useMemo<Option[]>(() => {
    if (!active) return [];
    const seen = new Map<number, Option>();
    for (const acc of accounts) {
      if (acc.brandName !== active.brandName) continue;
      if (!allCategories && acc.category.id !== active.category.id) continue;
      if (seen.has(acc.geography.id)) continue;
      seen.set(acc.geography.id, {
        label: acc.geography.name,
        accountBrandId: acc.accountBrandId,
        active: !allRegions && acc.geography.id === active.geography.id,
      });
    }
    const opts = Array.from(seen.values());
    if (opts.length > 1) {
      opts.unshift({
        label: "All regions",
        accountBrandId: ALL_ID,
        active: allRegions,
      });
    }
    return opts;
  }, [accounts, active, allCategories, allRegions]);

  if (!active) return null;

  const handleSelect = (accountBrandId: number) => {
    if (accountBrandId === active.accountBrandId) return;
    resetFilters();
    activate(accountBrandId);
  };

  const handleBrand = (accountBrandId: number) => {
    const picked = accounts.find((a) => a.accountBrandId === accountBrandId);
    if (!picked) return;
    const target = pickAccount(accounts, active, { brand: picked.brandName });
    if (target) {
      setAllCategories(false);
      setAllRegions(false);
      handleSelect(target.accountBrandId);
    }
  };

  const handleCategory = (accountBrandId: number) => {
    if (accountBrandId === ALL_ID) {
      setAllCategories(true);
      return;
    }
    const picked = accounts.find((a) => a.accountBrandId === accountBrandId);
    if (!picked) return;
    const target = pickAccount(accounts, active, {
      categoryId: picked.category.id,
    });
    if (target) {
      setAllCategories(false);
      handleSelect(target.accountBrandId);
    }
  };

  const handleGeography = (accountBrandId: number) => {
    if (accountBrandId === ALL_ID) {
      setAllRegions(true);
      return;
    }
    setAllRegions(false);
    handleSelect(accountBrandId);
  };

  return (
    <div className={styles.root}>
      <Segment
        label={active.brandName}
        options={brandOptions}
        onSelect={handleBrand}
      />
      <span className={styles.separator}>|</span>
      <Segment
        label={allCategories ? "All categories" : active.category.name}
        options={categoryOptions}
        onSelect={handleCategory}
      />
      <span className={styles.separator}>|</span>
      <Segment
        label={allRegions ? "All regions" : active.geography.name}
        options={geographyOptions}
        onSelect={handleGeography}
      />
    </div>
  );
};
